/* eslint-disable react/jsx-pascal-case */
import { ActionsButton } from '@dealstack/frontend/shared/components/Table/MantineTable/ActionsButton/ActionsButton';
import { ToggleFiltersButton } from '@dealstack/frontend/shared/components/Table/MantineTable/ToggleFiltersButton/ToggleFiltersButton';
import { ToggleFullScreenButton } from '@dealstack/frontend/shared/components/Table/MantineTable/ToggleFullScreenButton/ToggleFullScreenButton';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { RecordAny } from '@dealstack/shared/lib/common/common';
import { ActionIcon, Button, Flex, rem, Text, useMantineTheme } from '@mantine/core';
import { Table } from '@tanstack/react-table';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { InfoBoxContainer } from '@dealstack/frontend/shared/components/InfoBox/InfoBox';
import { SelectionHeader } from '@dealstack/frontend/shared/components/Table/MantineTable/BulkActionsMenu/SelectionHeader/SelectionHeader.tsx';
import { FILTER_DEBOUNCE_MS } from '@dealstack/frontend/shared/components/Table/MantineTable/constants';
import { QuickFiltersSelect } from '@dealstack/frontend/shared/components/Table/MantineTable/QuickFiltersSelect/QuickFiltersSelect';
import { getTableFilterMeta } from '@dealstack/frontend/shared/components/Table/MantineTable/types';
import { GENERIC_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds.ts';
import { useClickOutside } from '@mantine/hooks';
import debounce from 'lodash/debounce';
import { BulkActionGroup } from '@dealstack/frontend/shared/components/Table/MantineTable/ActionsButton/ActionsButton.types';
import { QuickFilterOption } from '@dealstack/frontend/shared/components/Table/MantineTable/QuickFiltersSelect/QuickFiltersSelect.types';
import { ShowHideColumnsButton } from '@dealstack/frontend/shared/components/Table/MantineTable/ShowHideColumnsButton/ShowHideColumnsButton';

interface Props<T extends RecordAny> {
	table: Table<T>;
	actions?: (table: Table<T>) => BulkActionGroup<T>;
	customSettingsRenderer?: (table: Table<T>) => JSX.Element;
	customFilters?: JSX.Element[];
	renderBulkActionsMenu?: boolean;
	showDeletedTable?: boolean;
	returnToTable?: () => void;
	enableSearchBar?: boolean;
	enableFullScreenToggle?: boolean;

	actionsMode?: 'default' | 'select' | 'action';
	onActionsModeChange: (mode: 'default' | 'select' | 'action') => void;
	showRowSelection?: boolean;
	onShowRowSelectionChange: (show: boolean) => void;
	onActionCanceled: () => void;
	showSelectionHeader: boolean;

	quickFilterOptions?: QuickFilterOption[];
	quickFilters?: Record<string, boolean | string>;
	onQuickFiltersChange?: (value: Record<string, boolean | string>) => void;

	// Legacy support - will be removed
	bulkPrimaryActions?: (table: Table<T>) => JSX.Element[];
	bulkSecondaryActions?: (table: Table<T>) => JSX.Element[];
	bulkSpecialActions?: (table: Table<T>) => JSX.Element[];
}

const FilterWrapper = styled.div`
	display: flex;
	width: 100%;

	input {
		font-weight: 600;
		margin: 0;
		padding: 0;
		background-color: transparent;
		${({ theme }) => `color: ${theme.v3.colors.blue[6]};`}

		&::placeholder {
			${({ theme }) => `color: ${theme.v3.colors.blue[6]} !important;`}
			font-weight: 600;
		}

		&:focus {
			box-shadow: none !important;
		}
	}
`;

export const MantineTopToolbar = <T extends RecordAny>({
	table,
	actions,
	customFilters,
	customSettingsRenderer,
	renderBulkActionsMenu,
	showDeletedTable,
	returnToTable,
	enableSearchBar = true,
	actionsMode = 'default',
	onActionsModeChange,
	showRowSelection = false,
	onShowRowSelectionChange,
	onActionCanceled,
	showSelectionHeader = false,
	quickFilterOptions,
	quickFilters,
	onQuickFiltersChange = () => undefined,
	enableFullScreenToggle = true,
}: Props<T>): JSX.Element => {
	const tCommonUi = useTranslationCommonUi();

	const globalTheme = useMantineTheme();

	// Access table state properties directly to ensure re-renders when state changes
	const globalFilter = table.getState().globalFilter;
	const showColumnFilters = getTableFilterMeta(table).showColumnFilters;

	const globalFilterInputWrapperRef = useRef<HTMLDivElement>(null);
	const [showFilter, setShowFilter] = useState(false);
	const searchContainerRef = useClickOutside(() => setShowFilter(false));
	const globalFilterValue = (globalFilter as string) ?? '';
	const [localSearchValue, setLocalSearchValue] = useState(globalFilterValue);

	const debouncedSetGlobalFilter = useMemo(
		() => debounce((value: string) => table.setGlobalFilter(value), FILTER_DEBOUNCE_MS),
		[table]
	);

	useEffect(() => () => debouncedSetGlobalFilter.cancel(), [debouncedSetGlobalFilter]);

	// Sync local input when the global filter is cleared externally
	// (e.g. "Clear all" or removing a filter pill in AppliedFiltersSubrow)
	useEffect(() => {
		setLocalSearchValue(globalFilterValue);
	}, [globalFilterValue]);

	const toggleShowFilter = () => {
		setShowFilter((prev) => !prev);
		setTimeout(() => {
			if (globalFilterInputWrapperRef.current) {
				// TODO: rather than querySelector, let's try to add a ref to the input, if possible
				const inputElement = globalFilterInputWrapperRef.current.querySelector('input');
				if (inputElement) {
					inputElement.focus();
				}
			}
		}, 100);
	};

	return (
		<>
			{showDeletedTable && (
				<InfoBoxContainer
					direction="row"
					justify="space-between"
					align="center"
					gap="sm"
					mb="sm"
				>
					<Flex align="center" gap="xs">
						<Icon
							variant="deleted-table"
							size="logo"
							style={{ width: rem('40px'), height: rem('40px') }}
						/>
						<Text fw={600} fz={rem('14px')}>
							{tCommonUi('mantine_table.show_deleted')}
						</Text>
					</Flex>

					<Button variant="text" onClick={returnToTable} c="dark.0">
						{tCommonUi('mantine_table.return_to_table')}
					</Button>
				</InfoBoxContainer>
			)}
			<Flex
				sx={(theme) => ({
					borderRadius: '0.25rem 0.25rem 0 0',
					flexDirection: 'row',
					gap: theme.spacing.sm,
					justifyContent: 'space-between',
					alignItems: 'stretch',
					backgroundColor: globalTheme.colors.gray[0],
					border: `1px solid ${globalTheme.colors.gray[4]}`,
				})}
			>
				<Flex align="stretch" gap="xs" style={{ flex: 1, overflow: 'hidden' }}>
					{renderBulkActionsMenu && (
						<ActionsButton
							table={table}
							actions={actions}
							showSpecialActions={showDeletedTable}
							actionsMode={actionsMode}
							onActionsModeChange={onActionsModeChange}
							showRowSelection={showRowSelection}
							onShowRowSelectionChange={onShowRowSelectionChange}
							onActionCanceled={onActionCanceled}
						/>
					)}

					{enableSearchBar ? (
						<Flex
							align="center"
							style={{
								flex: 1,
								display: 'flex',
								height: '36px',
								padding: globalTheme.spacing.xs,
								margin: 'auto',
								overflow: 'hidden',
								borderRadius: rem('4px'),
								cursor: 'pointer',
								...(showFilter
									? {
											backgroundColor: globalTheme.colors.white[0],
											border: `1px solid ${globalTheme.colors.gray[4]}`,
										}
									: {}),
								...(renderBulkActionsMenu ? {} : { marginLeft: '1rem' }),
							}}
							onClick={toggleShowFilter}
							ref={searchContainerRef}
						>
							<Icon
								variant="search-v3"
								size="xl"
								color={globalTheme.colors.blue[6]}
							/>

							{showFilter ? null : (
								<Flex align="center" style={{ overflow: 'hidden' }}>
									<Text
										c={globalTheme.colors.dark[0]}
										fw={400}
										sx={(theme) => ({
											paddingLeft: theme.spacing.xs,
										})}
										style={{
											whiteSpace: 'nowrap',
										}}
									>
										{localSearchValue ||
											tCommonUi('mantine_table.search_input_placeholder')}
									</Text>
								</Flex>
							)}
							{showFilter && (
								<FilterWrapper
									ref={globalFilterInputWrapperRef}
									onClick={(ev) => ev.stopPropagation()}
									style={{ flex: 1 }}
								>
									<input
										placeholder={tCommonUi(
											'mantine_table.search_input_placeholder'
										)}
										value={localSearchValue}
										onChange={(e) => {
											const value = e.target.value ?? '';
											setLocalSearchValue(value);
											debouncedSetGlobalFilter(value);
										}}
										style={{
											width: '100%',
											border: 'none',
											outline: 'none',
											paddingLeft: rem('8px'),
										}}
									/>
									<ActionIcon
										variant="transparent"
										onClick={() => {
											debouncedSetGlobalFilter.cancel();
											setLocalSearchValue('');
											table.setGlobalFilter('');
										}}
									>
										{localSearchValue && (
											<Icon
												variant="close-v3"
												size="md"
												color={globalTheme.colors.black[0]}
											/>
										)}
									</ActionIcon>
								</FilterWrapper>
							)}
						</Flex>
					) : (
						<div style={{ flex: 1 }} />
					)}
				</Flex>
				<Flex align="center">
					{customFilters?.length && showColumnFilters ? customFilters : null}
					<ToggleFiltersButton
						table={table}
						leftSection={<Icon variant="filter" size="xl" />}
						data-testid={GENERIC_ELEMENT_ID.MANTINE_TABLE_FILTER_TOGGLE}
					/>
					<ShowHideColumnsButton
						table={table}
						customSettingsRenderer={customSettingsRenderer}
						leftSection={<Icon variant="settings" size="xl" />}
					/>
					{quickFilterOptions && quickFilterOptions.length > 0 && (
						<QuickFiltersSelect
							options={quickFilterOptions}
							value={quickFilters ?? {}}
							onChange={onQuickFiltersChange}
						/>
					)}
					{enableFullScreenToggle && (
						<ToggleFullScreenButton
							table={table}
							sx={() => ({
								borderTopRightRadius: `${rem('8px')} !important`,
								borderBottomRightRadius: `${rem('8px')} !important`,
							})}
						/>
					)}
				</Flex>
			</Flex>
			{showSelectionHeader ? (
				<Flex>
					<SelectionHeader table={table} />
				</Flex>
			) : null}
		</>
	);
};
